// Статистика партии: ACPL + Accuracy% (формула Lichess, гармоническое
// среднее) по сторонам + win%-граф. Порт gameStats из app.js v3 (~2475).
import type { GameNode } from './types';
import { nodeEvalKey, winPct } from './evals';
import { mainLineOf } from './tree';
import { classifyMove, type ClassifyCtx, type MoveQuality } from './classify';

export interface SideStats {
  acpl: number | null;
  accuracy: number | null;
  counts: Partial<Record<MoveQuality, number>>;
}

export interface GameStatsResult {
  w: SideStats;
  b: SideStats;
  /** win% по полуходам (начиная со стартовой позиции). */
  graph: number[];
}

export function gameStats(root: GameNode, ctx: ClassifyCtx): GameStatsResult {
  const nodes = mainLineOf(root);
  const side = {
    w: { loss: 0, n: 0, accs: [] as number[], counts: {} as Partial<Record<MoveQuality, number>> },
    b: { loss: 0, n: 0, accs: [] as number[], counts: {} as Partial<Record<MoveQuality, number>> },
  };
  const rootCp = ctx.evals.get(nodeEvalKey(root));
  const graph: number[] = [rootCp != null ? winPct(rootCp) : 50];
  for (const node of nodes) {
    const cpW = ctx.evals.get(nodeEvalKey(node));
    graph.push(cpW != null ? winPct(cpW) : graph[graph.length - 1]!);
    const p = node.parent!;
    const beforeW = ctx.evals.get(nodeEvalKey(p));
    const S = side[p.turn];
    if (beforeW != null && cpW != null) {
      const mB = p.turn === 'w' ? beforeW : -beforeW;
      const mA = p.turn === 'w' ? cpW : -cpW;
      S.loss += Math.min(1000, Math.max(0, mB - mA));
      const wB = winPct(mB);
      const wA = winPct(mA);
      S.accs.push(Math.max(0, Math.min(100, 103.1668 * Math.exp(-0.04354 * (wB - wA)) - 3.1669)));
      S.n++;
    }
    const cls = classifyMove(node, ctx);
    if (cls) S.counts[cls] = (S.counts[cls] || 0) + 1;
  }
  const harm = (a: number[]) => (a.length ? a.length / a.reduce((s, x) => s + 1 / Math.max(0.5, x), 0) : null);
  const pack = (S: (typeof side)['w']): SideStats => ({
    acpl: S.n ? Math.round(S.loss / S.n) : null,
    accuracy: harm(S.accs),
    counts: S.counts,
  });
  return { w: pack(side.w), b: pack(side.b), graph };
}

/** Ключевые моменты партии: ходы с пометкой качества, приоритет по тяжести. */
export const Q_PRIORITY: Partial<Record<MoveQuality, number>> = {
  brilliant: 9, blunder: 8, miss: 7, mistake: 6, great: 5, inaccuracy: 4, excellent: 3, good: 2,
};

export interface KeyMoment {
  node: GameNode;
  cls: MoveQuality;
  before: number | null;
  after: number | null;
}

export function collectKeyMoments(root: GameNode, ctx: ClassifyCtx, limit = 8): KeyMoment[] {
  const out: KeyMoment[] = [];
  for (const node of mainLineOf(root)) {
    const cls = classifyMove(node, ctx);
    if (!cls || cls === 'best' || cls === 'book' || cls === 'forced') continue;
    out.push({
      node,
      cls,
      before: ctx.evals.get(nodeEvalKey(node.parent!)),
      after: ctx.evals.get(nodeEvalKey(node)),
    });
  }
  out.sort((a, b) => (Q_PRIORITY[b.cls] || 0) - (Q_PRIORITY[a.cls] || 0));
  return out.slice(0, limit).sort((a, b) => (a.node.moveNo ?? 0) - (b.node.moveNo ?? 0));
}
